import { BarElement, BarProps, ChartData, Plugin } from "chart.js";
import { t } from "i18next";
import { Bar } from "react-chartjs-2";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { getTicks, getTooltip } from "../../utils/chartjsTheming";

export type PartyValueExtended = {
  partyId: string;
  partyName: string;
  label?: string;
  client: boolean;
  netResult: {
    average: number;
    averageLabel?: string;
    best?: number;
    worst?: number;
  };
};

/**
 * Two-entry chart data for a single party's net-result range without vs with a
 * settlement — used by the settlement card and the offer info modal. Returns
 * undefined when either side's stats are missing.
 */
export function settlementImpactData(
  baseline: PartyValueExtended["netResult"] | undefined,
  settled: PartyValueExtended["netResult"] | undefined,
): PartyValueExtended[] | undefined {
  if (!baseline || !settled) return undefined;
  // if all values are the same, there is only one net result
  const onlyOneNetResult =
    settled.best === settled.average && settled.worst === settled.average;
  return [
    {
      partyId: "baseline",
      partyName: t("case.analytics.settlement.netResultChart.noSettlement"),
      label: t("case.analytics.settlement.netResultChart.noSettlement"),
      client: false,
      netResult: baseline,
    },
    {
      partyId: "settled",
      partyName: t("case.analytics.settlement.netResultChart.withSettlement"),
      label: t("case.analytics.settlement.netResultChart.withSettlement"),
      client: false,
      // if there is only one result, skip best/worst
      netResult: onlyOneNetResult
        ? {
            average: settled.average,
            averageLabel: t(
              "case.analytics.settlement.netResultChart.onlyOneNetResult",
            ),
          }
        : settled,
    },
  ];
}

export const NetResultChart = ({
  data,
  selectedPartyId,
  onSelected,
  compact = false,
}: {
  data: PartyValueExtended[];
  selectedPartyId?: string;
  onSelected: (partyId: string) => void;
  // abbreviate y-axis tick labels (£1M, £400K) to save width on small charts
  compact?: boolean;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();

  const getDarkColor = (value: number) =>
    value < 0
      ? theme.palette.data.negative.dark
      : theme.palette.data.positive.dark;

  const labels = data.map(
    ({ client, partyName, label }) =>
      label ??
      t(
        client ? "case.analytics.name.client" : "case.analytics.name.noClient",
        { name: partyName },
      ),
  );

  // Floating bar data structure [worst, best]
  const barData: [number, number][] = data.map(({ netResult }) => {
    const worst = netResult.worst ?? netResult.average;
    const best = netResult.best ?? netResult.average;
    return [worst, best];
  });

  const backgroundColors = data.map(
    ({ partyId, netResult }) =>
      (netResult.average < 0
        ? theme.palette.data.negative.main
        : theme.palette.data.positive.main) +
      (partyId !== selectedPartyId ? "50" : "99"),
  );
  const borderColors = data.map(({ partyId, netResult }) =>
    partyId === selectedPartyId
      ? getDarkColor(netResult.average)
      : getDarkColor(netResult.average) + "80",
  );

  const dataset = {
    label: "Net result range",
    data: barData,
    backgroundColor: backgroundColors,
    borderColor: borderColors,
    borderWidth: data.map((party) =>
      party.partyId === selectedPartyId ? 2 : 0,
    ),
    borderSkipped: false,
  };

  const chartData: ChartData<"bar", (number | [number, number])[]> = {
    labels,
    datasets: [dataset],
  };

  // Custom plugin to draw the average net result line.
  const averageLinePlugin: Plugin<"bar"> = {
    id: "averageLinePlugin",
    afterDatasetsDraw: (chart) => {
      const {
        ctx,
        scales: { y: yScale },
        canvas,
      } = chart;
      // store lines for net result average values of each party
      const averageLines: {
        startX: number;
        endX: number;
        y: number;
        partyId: string;
      }[] = [];

      data.forEach(({ partyId, netResult }, index) => {
        const y = yScale.getPixelForValue(netResult.average);
        const meta = chart.getDatasetMeta(0).data[index] as
          (BarElement & Partial<BarProps>) | undefined;
        if (!meta) return;

        const { x } = meta.getProps(["x"], true);
        if (x === null) return;
        const barWidth = meta.width || 10;
        const lineLength = 1.2 * barWidth;
        const startX = x - lineLength / 2;
        const endX = startX + lineLength;

        averageLines.push({ startX, endX, y, partyId });

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(startX, y);
        ctx.lineTo(endX, y);
        ctx.strokeStyle = getDarkColor(netResult.average);
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.restore();
      });

      canvas.onclick = ({ clientX, clientY }) => {
        const rect = canvas.getBoundingClientRect();
        const mouseX = clientX - rect.left;
        const mouseY = clientY - rect.top;

        const clickedLine = averageLines.find(
          ({ startX, endX, y }) =>
            mouseX >= startX && mouseX <= endX && Math.abs(mouseY - y) < 5,
        );

        if (clickedLine) onSelected(clickedLine.partyId);
      };
    },
  };

  return (
    <Bar
      data={chartData}
      options={{
        onClick: (_, elements) => {
          if (!elements || elements.length === 0) return;
          const selectedIndex = elements[0].index;
          onSelected(data[selectedIndex].partyId);
        },
        animation: false,
        interaction: {
          mode: "index",
          intersect: true,
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            ...getTooltip(theme),
            position: "nearest",
            mode: "nearest",
            intersect: false,
            caretSize: 0,
            displayColors: false,
            callbacks: {
              title: (items) => {
                // there should only be one ite
                if (items.length !== 1) return;
                const { partyName, label } = data[items[0].dataIndex];
                return (
                  label ??
                  t("case.analytics.netResult.chart.tooltip.title", {
                    name: partyName,
                  })
                );
              },
              label: (item) => {
                const { netResult } = data[item.dataIndex];
                const labels = netResult.averageLabel
                  ? [
                      t(
                        "case.analytics.netResult.chart.tooltip.averageLabeled",
                        {
                          label: netResult.averageLabel,
                          value: currency.format(netResult.average),
                        },
                      ),
                    ]
                  : [
                      t("case.analytics.netResult.chart.tooltip.average", {
                        value: currency.format(netResult.average),
                      }),
                    ];
                if (netResult.worst)
                  labels.push(
                    t("case.analytics.netResult.chart.tooltip.worst", {
                      value: currency.format(netResult.worst),
                    }),
                  );
                if (netResult.best)
                  labels.push(
                    t("case.analytics.netResult.chart.tooltip.best", {
                      value: currency.format(netResult.best),
                    }),
                  );
                return labels;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              ...getTicks(theme),
              callback: (value) =>
                typeof value === "number"
                  ? compact
                    ? currency.formatCompact(value)
                    : currency.format(value)
                  : value,
            },
            grid: {
              lineWidth: (context) => (context.tick.value === 0 ? 2 : 1),
            },
          },
          x: {
            grid: { color: () => undefined },
            ticks: getTicks(theme),
          },
        },
      }}
      plugins={[averageLinePlugin]}
    />
  );
};
